/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import { NO_MARGIN_BOTTOM } from '@constants';
import { ContentModuleModal, SortableList, ColorField, TextField } from '@molecules';

import Form from '../Form';
import { FORM_FIELD, INITIAL_VALUES } from './constants';
import Item from './Item';
import FAQForm from './FAQForm';
import FAQItem from './FAQItem';
import { Typography } from '@components/atoms';
import { noop } from 'lodash';
import useSetSingle from '@hooks/useSetSingle';
import FAQFormEdit from './FAQFormEdit';

const FAQModule = ({ visible = true, onSubmit, hide, previewImg, onDelete, initialValues: initData, errors }) => {
  const [form] = Form.useForm(FORM_FIELD);
  const initialValues = React.useMemo(() => form.deserialize(initData || INITIAL_VALUES), [form, initData]);

  const [questions, setQuestions] = React.useState(
    initialValues[FORM_FIELD.QUESTIONS.name] ? initialValues[FORM_FIELD.QUESTIONS.name] : [],
  );

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleClose = () => {
    setQuestions([]);
    form.resetFields();
    hide();
  };

  const syncQuestions = React.useCallback(
    (_questions) => {
      setQuestions(_questions);
      form.setFields([{ name: [FORM_FIELD.QUESTIONS.name], value: _questions }]);
    },
    [form, setQuestions],
  );

  const handleAdd = React.useCallback(
    (question) => {
      return syncQuestions([...form.getFieldValue(FORM_FIELD.QUESTIONS.name), question]);
    },
    [form, syncQuestions],
  );

  return (
    <ContentModuleModal
      visible={visible}
      title="FAQ"
      subtitle="Upload your questions"
      okText={onDelete ? 'SAVE CHANGES' : 'ADD FAQ'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Typography.Title level={3}>Add a new question</Typography.Title>
      <FAQForm onSubmit={handleAdd} />
      <SortableList
        style={NO_MARGIN_BOTTOM}
        withPlaceholder
        value={[...questions]}
        label="Current questions"
        withCount
        onChange={syncQuestions}
        useEditable={useSetSingle} // Limit to one edition at a time
      >
        {(value, { edit, isEdit, save, buttons }) =>
          isEdit ? (
            <FAQFormEdit onSubmit={save} initialValues={value} buttons={buttons} />
          ) : (
            <FAQItem item={value} handleEdit={edit}>
              {value?.title}
            </FAQItem>
          )
        }
      </SortableList>

      <Form
        form={form}
        onSerializedFinish={handleSubmit}
        layout="vertical"
        colon={false}
        wrapperCol={{ span: 24 }}
        initialValues={initialValues}
      >
        <TextField {...FORM_FIELD.TITLE} style={{ marginTop: 20 }} />
        <TextField {...FORM_FIELD.DESCRIPTION} />
        <TextField {...FORM_FIELD.URL} />
        <TextField {...FORM_FIELD.URL_TEXT} />
        <ColorField {...FORM_FIELD.BACKGROUND_COLOUR} />
        <Form.List {...FORM_FIELD.QUESTIONS} style={{ marginBottom: 20 }}>
          {noop}
        </Form.List>
      </Form>
    </ContentModuleModal>
  );
};

FAQModule.Item = Item;

export default FAQModule;
